import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Lock, CreditCard, Check, Star } from 'lucide-react';
import { Button, CHECKOUT_URL } from '../ui/Button';
import { CountdownTimer } from '../ui/CountdownTimer';
const BASE_CHECKOUT_URL = 'https://checkout.ebookpoint.shop/cart/59619171893329:1';
const PRO_CHECKOUT_URL = 'https://checkout.ebookpoint.shop/cart/59618889433169:1';
interface Plan {
  id: 'base' | 'pro';
  title: string;
  price: string;
  oldPrice: string;
  url: string;
  badge?: string;
  includes?: string[];
}
const plans: Plan[] = [{
  id: 'base',
  title: 'Dominá tu primer triatlón',
  price: '$19.990',
  oldPrice: '$49.990',
  url: BASE_CHECKOUT_URL
}, {
  id: 'pro',
  title: 'Dominá tu primer triatlón + Triatlón en 6HS',
  price: '$27.990',
  oldPrice: '$59.990',
  url: PRO_CHECKOUT_URL,
  badge: 'Más completo',
  includes: ['Bono ELITE: Triatlón en 6HS. Para atletas con poco tiempo pero con disciplina.']
}];
export function Pricing() {
  const [selected, setSelected] = useState<'base' | 'pro'>('base');
  const activePlan = plans.find(p => p.id === selected)!;
  return <section id="comprar" className="py-20 bg-brand-dark relative overflow-hidden" data-id="element-88">
      {/* subtle accent glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-green/5 rounded-full blur-3xl pointer-events-none" data-id="element-89" />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative" data-id="element-90">
        <div className="text-center mb-8" data-id="element-91">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-brand-green text-brand-dark text-xs font-bold tracking-wider uppercase rounded-full mb-4" data-id="element-92">
            ⚡ OFERTA POR TIEMPO LIMITADO
          </span>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-2" data-id="element-93">
            Elegí tu acceso
          </h2>
          <p className="text-white/70 max-w-xl mx-auto" data-id="element-94">
            Seleccioná el plan que mejor se adapte a vos.
          </p>
        </div>

        {/* Countdown - dark variant */}
        <div className="max-w-md mx-auto mb-8" data-id="element-95">
          <CountdownTimer variant="dark" data-id="element-96" />
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="bg-white rounded-3xl shadow-2xl shadow-brand-green/10 p-5 md:p-6" data-id="element-97">
          {/* Selectable plan options */}
          <div role="radiogroup" aria-label="Planes" className="grid sm:grid-cols-2 gap-3 mb-5" data-id="element-98">
            {plans.map(plan => {
            const isActive = selected === plan.id;
            return <button key={plan.id} role="radio" aria-checked={isActive} onClick={() => setSelected(plan.id)} className={`relative text-left rounded-2xl border-2 p-4 transition-all ${isActive ? 'border-brand-green bg-brand-green/5' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-id="element-99">
                  {plan.badge && <span className="absolute -top-2.5 right-3 inline-flex items-center gap-1 bg-brand-dark text-brand-green text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full" data-id="element-100">
                      <Star size={10} fill="currentColor" data-id="element-101" />
                      {plan.badge}
                    </span>}

                  <div className="flex items-start gap-3" data-id="element-102">
                    {/* Radio indicator */}
                    <span className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${isActive ? 'border-brand-green bg-brand-green' : 'border-slate-300'}`} data-id="element-103">
                      {isActive && <Check size={12} className="text-white" strokeWidth={3} data-id="element-104" />}
                    </span>

                    <div className="flex-1 min-w-0" data-id="element-105">
                      <p className="font-bold text-brand-dark text-sm leading-snug mb-1" data-id="element-106">
                        {plan.title}
                      </p>
                      <div className="flex items-baseline gap-2" data-id="element-107">
                        <span className="font-serif text-2xl font-bold text-brand-dark" data-id="element-108">
                          {plan.price}
                        </span>
                        <span className="text-slate-400 line-through text-sm" data-id="element-109">
                          {plan.oldPrice}
                        </span>
                      </div>

                      {plan.includes && <div className="mt-2 pt-2 border-t border-slate-200/70" data-id="element-110">
                          <p className="text-[11px] font-bold text-brand-green-dark uppercase tracking-wider mb-1" data-id="element-111">
                            Incluye
                          </p>
                          <ul className="space-y-1" data-id="element-112">
                            {plan.includes.map((inc, i) => <li key={i} className="flex items-start gap-1.5 text-xs text-slate-600 leading-snug" data-id="element-113">
                                <Check size={13} className="text-brand-green-dark flex-shrink-0 mt-0.5" data-id="element-114" />
                                <span data-id="element-115">{inc}</span>
                              </li>)}
                          </ul>
                        </div>}
                    </div>
                  </div>
                </button>;
          })}
          </div>

          {/* Shared CTA */}
          <Button href={activePlan.url} className="w-full px-6 py-4 text-base md:text-lg mb-3" data-id="element-116">
            → QUIERO DESBLOQUEAR MI PRIMER TRIATLÓN — {activePlan.price}
          </Button>

          <div className="flex flex-wrap justify-center items-center gap-2 text-xs text-slate-500 font-medium" data-id="element-117">
            <div className="flex items-center gap-1" data-id="element-118">
              <ShieldCheck size={14} className="text-brand-green-dark" data-id="element-119" />
              Garantía 7 días
            </div>
            <span className="text-slate-300" data-id="element-120">•</span>
            <div className="flex items-center gap-1" data-id="element-121">
              <Lock size={14} className="text-brand-green-dark" data-id="element-122" />
              Pago seguro
            </div>
            <span className="text-slate-300" data-id="element-123">•</span>
            <div className="flex items-center gap-1" data-id="element-124">
              <CreditCard size={14} className="text-brand-green-dark" data-id="element-125" />
              Mercado Pago
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}